import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Alert, Checkbox, FormControlLabel,
  IconButton, Menu, MenuItem, InputAdornment,
  useMediaQuery, useTheme,
} from '@mui/material';
import { SPACING, BORDER_RADIUS, ELEVATION, BRAND_COLORS } from '../styles/testCycleDesignTokens';
import MenuIcon from '@mui/icons-material/Menu';
import {
  createTestCycle, updateTestCycle, getTestCycleDetails, setCollections,
  checkDuplicateName, invalidateTestCycleDetailsCache,
} from '../services/testCycleService';
import { apiService } from '../services/apiService';

const DASHBOARD_RECENTS_EVENT = 'dashboard-recent-updated';

interface CollectionItem {
  id: number;
  name: string;
  parentCollectionId?: number | null;
  displayOrder?: number;
}

const toCollectionId = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

interface TestCycleWizardProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  cycleId?: number | null;
  testPlanCreationBlocked?: boolean;
  testPlanCreationBlockedMessage?: string;
}

const TestCycleWizard: React.FC<TestCycleWizardProps> = ({
  open,
  onClose,
  projectId,
  cycleId = null,
  testPlanCreationBlocked = false,
  testPlanCreationBlockedMessage = 'Test plan limit reached. Upgrade plan or delete existing.',
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [collectionsList, setCollectionsList] = useState<CollectionItem[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [collectionMenuAnchor, setCollectionMenuAnchor] = useState<null | HTMLElement>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const selectedCollectionSet = useMemo(
    () => new Set(selectedCollections),
    [selectedCollections],
  );

  const collectionById = useMemo(() => {
    const map = new Map<number, CollectionItem>();
    collectionsList.forEach((c) => map.set(c.id, c));
    return map;
  }, [collectionsList]);

  const subCollectionsByParentIdFull = useMemo(() => {
    const childrenByParentId: Record<number, CollectionItem[]> = {};
    collectionsList.forEach((c) => {
      const parentId = toCollectionId(c.parentCollectionId);
      if (parentId != null && collectionById.has(parentId)) {
        if (!childrenByParentId[parentId]) childrenByParentId[parentId] = [];
        childrenByParentId[parentId].push(c);
      }
    });
    Object.keys(childrenByParentId).forEach((pid) => {
      childrenByParentId[Number(pid)].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    });
    return childrenByParentId;
  }, [collectionsList, collectionById]);

  const getDescendantIds = useCallback(
    (parentId: number): number[] => {
      const walk = (id: number): number[] => {
        const children = subCollectionsByParentIdFull[id] || [];
        return children.flatMap((c) => [c.id, ...walk(c.id)]);
      };
      return walk(parentId);
    },
    [subCollectionsByParentIdFull]
  );

  /** Walk up to the top-level collection in this project. */
  const getRootCollectionId = useCallback(
    (colId: number): number => {
      let current = colId;
      let parentId = toCollectionId(collectionById.get(current)?.parentCollectionId);
      while (parentId != null && collectionById.has(parentId)) {
        current = parentId;
        parentId = toCollectionId(collectionById.get(current)?.parentCollectionId);
      }
      return current;
    },
    [collectionById]
  );

  const isRootCollection = useCallback(
    (colId: number): boolean => getRootCollectionId(colId) === colId,
    [getRootCollectionId]
  );

  const NAME_MAX_LENGTH = 35;
  const DESCRIPTION_MAX_LENGTH = 250;

  const sanitizeTestPlanNameWhileTyping = (value: string): string =>
    value.replace(/^\s+/, '').replace(/ {2,}/g, ' ').slice(0, NAME_MAX_LENGTH);

  const normalizeTestPlanNameForSave = (value: string): string =>
    value.trim().replace(/\s+/g, ' ').slice(0, NAME_MAX_LENGTH);

  const showFormError = useCallback((message: string) => {
    setError(message);
  }, []);

  const handleNameChange = (value: string) => {
    const sanitized = sanitizeTestPlanNameWhileTyping(value);
    setName(sanitized);
    if (value.replace(/^\s+/, '').replace(/ {2,}/g, ' ').length > NAME_MAX_LENGTH) {
      setNameError('Only 35 characters are allowed.');
    } else {
      setNameError(null);
    }
  };

  const handleDescriptionChange = (value: string) => {
    const trimmed = value.slice(0, DESCRIPTION_MAX_LENGTH);
    setDescription(trimmed);
    if (value.length > DESCRIPTION_MAX_LENGTH) {
      setDescriptionError('Only 250 characters are allowed.');
    } else {
      setDescriptionError(null);
    }
  };

  useEffect(() => {
    if (!open || !projectId) return;

    let isMounted = true;

    const fetchCollections = async () => {
      try {
        if (!projectId || typeof projectId !== 'number') {
          if (isMounted) setError('Project ID is required');
          return;
        }

        const collectionsRes = await apiService.getCollectionsByProject(projectId);
        if (!Array.isArray(collectionsRes)) {
          if (isMounted) setError('Failed to load collections: Invalid response format');
          return;
        }

        const collections: CollectionItem[] = collectionsRes
          .map((col: any) => {
            const id = toCollectionId(col?.id);
            if (id == null) return null;
            const displayOrder = Number(col?.displayOrder ?? col?.sortOrder ?? 0);
            return {
              id,
              name: String(col?.name ?? ''),
              parentCollectionId: toCollectionId(col.parentCollectionId ?? col.parentCollection?.id),
              displayOrder: Number.isFinite(displayOrder) ? displayOrder : 0,
            } as CollectionItem;
          })
          .filter((c: CollectionItem | null): c is CollectionItem => c != null);

        if (!isMounted) return;

        setCollectionsList(collections);

        if (cycleId && typeof cycleId === 'number') {
          try {
            const existingCycle = await getTestCycleDetails(projectId, cycleId);
            if (!existingCycle) {
              throw new Error('Test plan not found');
            }
            if (!isMounted) return;

            setName(sanitizeTestPlanNameWhileTyping(existingCycle.name || ''));
            const desc = (existingCycle.description || '').slice(0, DESCRIPTION_MAX_LENGTH);
            setDescription(desc);
            setDescriptionError(null);

            const selectedCols: number[] = [];
            if (existingCycle.collections) {
              existingCycle.collections.forEach((tcc: any) => {
                const id = toCollectionId(tcc.collection?.id);
                if (id != null) selectedCols.push(id);
              });
            }

            const byId = new Map(collections.map((c) => [c.id, c]));
            const childrenByParent: Record<number, number[]> = {};
            collections.forEach((c) => {
              const parentId = toCollectionId(c.parentCollectionId);
              if (parentId == null) return;
              if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
              childrenByParent[parentId].push(c.id);
            });
            const findRoot = (id: number): number => {
              let current = id;
              let parentId = toCollectionId(byId.get(current)?.parentCollectionId);
              while (parentId != null && byId.has(parentId)) {
                current = parentId;
                parentId = toCollectionId(byId.get(current)?.parentCollectionId);
              }
              return current;
            };
            const addDescendants = (parentId: number, into: Set<number>) => {
              for (const childId of childrenByParent[parentId] || []) {
                if (!into.has(childId)) {
                  into.add(childId);
                  addDescendants(childId, into);
                }
              }
            };

            // Promote any nested selections to their root and silently include all descendants.
            const expanded = new Set<number>();
            const roots = new Set(selectedCols.map(findRoot));
            roots.forEach((rootId) => {
              expanded.add(rootId);
              addDescendants(rootId, expanded);
            });
            setSelectedCollections(Array.from(expanded));
          } catch (err: any) {
            if (isMounted) {
              setError(`Failed to load test plan: ${err.message}`);
            }
          }
        } else if (isMounted) {
          setName('');
          setDescription('');
          setDescriptionError(null);
          setCollectionSearch('');
          setSortOrder(null);
          setSelectedCollections([]);
        }

        if (isMounted) {
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(`Failed to load collections: ${err.message}`);
        }
      }
    };

    fetchCollections();
    return () => {
      isMounted = false;
    };
  }, [open, projectId, cycleId]);

  const handleCollectionToggle = (colId: number) => {
    // UI only lists roots; always silently include/exclude nested collections.
    if (!isRootCollection(colId)) return;

    const isCurrentlySelected = selectedCollectionSet.has(colId);
    if (isCurrentlySelected) {
      const idsToRemove = new Set([colId, ...getDescendantIds(colId)]);
      setSelectedCollections((prev) => prev.filter((id) => !idsToRemove.has(id)));
    } else {
      const collectionsToSelect = [colId, ...getDescendantIds(colId)];
      setSelectedCollections((prev) => {
        const next = new Set(prev);
        collectionsToSelect.forEach((id) => next.add(id));
        return Array.from(next);
      });
      setError(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmedName = normalizeTestPlanNameForSave(name);
      if (!trimmedName) {
        showFormError('Name is required.');
        setSaving(false);
        return;
      }
      if (!cycleId && testPlanCreationBlocked) {
        showFormError(testPlanCreationBlockedMessage);
        setSaving(false);
        return;
      }
      if (trimmedName.length > NAME_MAX_LENGTH) {
        showFormError('Only 35 characters are allowed in the name. Please shorten it and try again.');
        setSaving(false);
        return;
      }
      const isDuplicate = await checkDuplicateName(projectId, trimmedName, cycleId ?? undefined);
      if (isDuplicate) {
        showFormError(`A test plan with the name "${trimmedName}" already exists in this project. Please choose a different name.`);
        setSaving(false);
        return;
      }
      if (selectedCollections.length === 0) {
        showFormError('Select at least one collection.');
        setSaving(false);
        return;
      }
      if (description.length > DESCRIPTION_MAX_LENGTH) {
        showFormError('Only 250 characters are allowed in the description. Please shorten it and try again.');
        setSaving(false);
        return;
      }

      let finalCycleId: number;

      if (cycleId) {
        await updateTestCycle(projectId, cycleId, { name: trimmedName, description });
        finalCycleId = cycleId;
      } else {
        const cycle = await createTestCycle(projectId, { name: trimmedName, description });
        if (!cycle?.id) {
          throw new Error('Invalid response from server: cycle ID is missing');
        }
        finalCycleId = cycle.id;
      }

      let orderedCollectionIds = selectedCollections;
      if (sortOrder) {
        const byId = new Map(collectionsList.map((c) => [c.id, c]));
        orderedCollectionIds = [...selectedCollections].sort((a, b) => {
          const nameA = byId.get(a)?.name ?? '';
          const nameB = byId.get(b)?.name ?? '';
          const cmp = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
          return sortOrder === 'asc' ? cmp : -cmp;
        });
      }

      await setCollections(finalCycleId, orderedCollectionIds);
      invalidateTestCycleDetailsCache(projectId, finalCycleId);

      setSaving(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(DASHBOARD_RECENTS_EVENT));
      }
      onClose();
    } catch (err: any) {
      setSaving(false);
      const data = err.response?.data;
      const errorCode = data?.errorCode;
      const backendMsg = data?.error || data?.message || err.message || '';
      const isPlanLimit =
        err.response?.status === 400 &&
        (errorCode === 'PLAN_LIMIT_EXCEEDED' ||
          (typeof backendMsg === 'string' &&
            (backendMsg.toLowerCase().includes('plan limit') ||
              backendMsg.toLowerCase().includes('test plan limit'))));
      const errorMessage = isPlanLimit
        ? testPlanCreationBlockedMessage
        : err.response?.status === 409 || data?.code === 'DUPLICATE_NAME'
          ? (data?.error || data?.message || `A test plan with the name "${name.trim()}" already exists in this project. Please choose a different name.`)
          : typeof backendMsg === 'string' && backendMsg.toLowerCase().includes('collection')
            ? 'Select at least one collection.'
            : (data?.error || data?.message || err.message || `Failed to ${cycleId ? 'update' : 'create'} test plan. Please try again.`);
      showFormError(errorMessage);
    }
  };

  const allRootCollections = useMemo(() => {
    return collectionsList.filter((c) => {
      const parentId = toCollectionId(c.parentCollectionId);
      return parentId == null || !collectionById.has(parentId);
    });
  }, [collectionsList, collectionById]);

  const visibleRootCollections = useMemo(() => {
    const query = collectionSearch.trim().toLowerCase();
    let roots = allRootCollections;

    if (query) {
      const matchingRootIds = new Set<number>();
      collectionsList.forEach((c) => {
        if (!c.name.toLowerCase().includes(query)) return;
        matchingRootIds.add(getRootCollectionId(c.id));
      });
      roots = allRootCollections.filter((c) => matchingRootIds.has(c.id));
    }

    if (!sortOrder) return roots;
    return [...roots].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [allRootCollections, collectionSearch, collectionsList, getRootCollectionId, sortOrder]);

  const handleSelectAllCollections = () => {
    setCollectionMenuAnchor(null);
    // Select every root and silently include all nested folders under them.
    const next = new Set<number>();
    allRootCollections.forEach((root) => {
      next.add(root.id);
      getDescendantIds(root.id).forEach((id) => next.add(id));
    });
    setSelectedCollections(Array.from(next));
  };

  const handleDeselectAllCollections = () => {
    setCollectionMenuAnchor(null);
    setSelectedCollections([]);
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const displayChildrenByParentId = useMemo(() => {
    const childrenByParentId: Record<number, CollectionItem[]> = {};
    collectionsList.forEach((c) => {
      const parentId = toCollectionId(c.parentCollectionId);
      if (parentId != null && collectionById.has(parentId)) {
        if (!childrenByParentId[parentId]) childrenByParentId[parentId] = [];
        childrenByParentId[parentId].push(c);
      }
    });
    Object.keys(childrenByParentId).forEach((pid) => {
      childrenByParentId[Number(pid)].sort(
        (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
    });
    return childrenByParentId;
  }, [collectionsList, collectionById]);

  const renderNestedCollections = (parentId: number, forceShow: boolean): React.ReactNode => {
    const children = displayChildrenByParentId[parentId] || [];
    if (children.length === 0) return null;
    const query = collectionSearch.trim().toLowerCase();
    const parent = collectionById.get(parentId);
    const parentMatches = (parent?.name ?? '').toLowerCase().includes(query);
    const showAll = forceShow || !query || parentMatches;
    const visible = showAll
      ? children
      : children.filter((child) => {
          if (child.name.toLowerCase().includes(query)) return true;
          const stack = [...(displayChildrenByParentId[child.id] || [])];
          while (stack.length > 0) {
            const next = stack.pop();
            if (!next) continue;
            if (next.name.toLowerCase().includes(query)) return true;
            stack.push(...(displayChildrenByParentId[next.id] || []));
          }
          return false;
        });
    if (visible.length === 0) return null;
    return visible.map((child) => {
      const childMatches = !query || child.name.toLowerCase().includes(query);
      return (
        <Box key={child.id} sx={{ pl: 2.5 }}>
          <Typography variant="body2" sx={{ py: 0.35, fontWeight: 600, color: 'text.primary' }}>
            {child.name}
          </Typography>
          {renderNestedCollections(child.id, showAll || childMatches)}
        </Box>
      );
    });
  };

  const renderRootCollectionRow = (col: CollectionItem) => {
    const nestedCount = getDescendantIds(col.id).length;
    const isChecked = selectedCollectionSet.has(col.id);

    return (
      <Box
        key={col.id}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: BORDER_RADIUS.sm,
          p: 1,
          mb: 1,
          bgcolor: 'grey.50',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={() => handleCollectionToggle(col.id)}
              inputProps={{ 'aria-label': col.name }}
            />
          }
          label={
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
              {col.name}
            </Typography>
          }
          sx={{ flex: 1, m: 0, alignItems: 'center' }}
        />
        {nestedCount > 0 ? (
          <Box sx={{ pl: 4.5, pb: 0.5 }}>{renderNestedCollections(col.id, false)}</Box>
        ) : null}
      </Box>
    );
  };

  const renderTree = () => {
    if (collectionsList.length === 0) {
      return (
        <Typography color="textSecondary" sx={{ mt: 2 }}>
          No collections found for this project.
        </Typography>
      );
    }

    if (visibleRootCollections.length === 0) {
      return (
        <Typography color="textSecondary" sx={{ mt: 2 }}>
          No collections match your search.
        </Typography>
      );
    }

    return (
      <Box sx={{ minHeight: 160, p: SPACING.sm, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ maxHeight: 320, overflowY: 'auto', flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: BORDER_RADIUS.sm, p: SPACING.sm }}>
          {visibleRootCollections.map((col) => renderRootCollectionRow(col))}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={fullScreen}
      fullScreen={fullScreen}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10,
        ...(fullScreen
          ? {}
          : {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: 8,
                pb: 2,
                boxSizing: 'border-box',
              },
            }),
      }}
      aria-labelledby="test-cycle-wizard-title"
      aria-describedby="test-cycle-wizard-description"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : BORDER_RADIUS.md,
          boxShadow: ELEVATION.modal,
          maxHeight: fullScreen ? '100%' : 'calc(100vh - 104px)',
          m: fullScreen ? 0 : '0 auto',
          ...(fullScreen
            ? {}
            : {
                width: 'min(520px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))',
                maxWidth: 'none',
                transform: 'translateX(calc(clamp(172px, 13.4375vw, 280px) / 2))',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }),
        },
      }}
    >
      <DialogTitle
        id="test-cycle-wizard-title"
        component="h2"
        sx={{
          fontWeight: 600,
          fontSize: '1.25rem',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: SPACING.md,
          px: SPACING.lg,
          flexShrink: 0,
        }}
      >
        {cycleId ? 'Edit Test Plan' : 'Create Test Plan'}
      </DialogTitle>
      {error && (
        <Alert
          severity="error"
          sx={{
            mx: SPACING.lg,
            mt: SPACING.md,
            mb: 0,
            flexShrink: 0,
            alignItems: 'flex-start',
            '& .MuiAlert-icon': { pt: '2px' },
            '& .MuiAlert-message': { py: 0.5, lineHeight: 1.4, overflow: 'visible', fontWeight: 500 },
          }}
          role="alert"
        >
          {error}
        </Alert>
      )}
      <DialogContent
        id="test-cycle-wizard-description"
        sx={{
          pt: SPACING.lg,
          px: SPACING.lg,
          display: 'flex',
          flexDirection: 'column',
          overflow: fullScreen ? 'hidden' : 'auto',
          flex: fullScreen ? undefined : '1 1 auto',
          minHeight: 0,
        }}
      >
        {!cycleId && testPlanCreationBlocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {testPlanCreationBlockedMessage}
          </Alert>
        )}
        <Box sx={{ mb: SPACING.lg }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            fullWidth
            margin="normal"
            size="small"
            sx={{ mb: 1 }}
            error={!!nameError}
            helperText={
              nameError ? (
                <Typography component="span" variant="caption" color="error">
                  {nameError}
                </Typography>
              ) : (
                <Typography component="span" variant="caption" color="text.secondary">
                  {name.length}/{NAME_MAX_LENGTH} characters
                </Typography>
              )
            }
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            size="small"
            error={!!descriptionError}
            helperText={
              descriptionError ? (
                <Typography component="span" variant="caption" color="error">
                  {descriptionError}
                </Typography>
              ) : (
                <Typography component="span" variant="caption" color="text.secondary">
                  {description.length}/{DESCRIPTION_MAX_LENGTH} characters
                </Typography>
              )
            }
            inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mb: SPACING.lg }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: BRAND_COLORS.darkBlue, mb: SPACING.sm }}
          >
            Select Collections
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: SPACING.sm }}>
            Select parent collections only. Nested sub-collections and their requests are included automatically.
          </Typography>
          <TextField
            placeholder="Search collections..."
            value={collectionSearch}
            onChange={(e) => setCollectionSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ mt: 1, mb: SPACING.sm }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCollectionMenuAnchor(e.currentTarget);
                    }}
                    edge="end"
                    aria-label="Collection options"
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Menu
            anchorReference="anchorPosition"
            anchorPosition={
              collectionMenuAnchor
                ? (() => {
                    const rect = collectionMenuAnchor.getBoundingClientRect();
                    return { top: rect.bottom, left: rect.right };
                  })()
                : undefined
            }
            open={Boolean(collectionMenuAnchor)}
            onClose={() => setCollectionMenuAnchor(null)}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            disableScrollLock
            sx={{ zIndex: (theme) => theme.zIndex.modal + 12 }}
            PaperProps={{
              sx: {
                minWidth: 190,
                zIndex: (theme) => theme.zIndex.modal + 12,
                '& .MuiMenuItem-root': { color: 'text.primary', fontWeight: 500 },
              },
            }}
          >
            <MenuItem onClick={() => { setSortOrder('asc'); setCollectionMenuAnchor(null); }}>
              Sort ascending
            </MenuItem>
            <MenuItem onClick={() => { setSortOrder('desc'); setCollectionMenuAnchor(null); }}>
              Sort descending
            </MenuItem>
            <MenuItem onClick={handleSelectAllCollections} disabled={allRootCollections.length === 0}>
              Select all collections
            </MenuItem>
            <MenuItem onClick={handleDeselectAllCollections} disabled={selectedCollections.length === 0}>
              Unselect all collections
            </MenuItem>
          </Menu>
          {renderTree()}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: SPACING.lg,
          py: SPACING.md,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: SPACING.sm,
          flexShrink: 0,
        }}
      >
        <Button onClick={onClose} disabled={saving} aria-label="Cancel" sx={{ textTransform: 'none', fontWeight: 500 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || (!cycleId && testPlanCreationBlocked)}
          aria-label={saving ? 'Saving' : 'Save'}
          sx={{ textTransform: 'none', fontWeight: 600, px: SPACING.lg, boxShadow: ELEVATION.card }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestCycleWizard;
